import { useMemo, useRef } from 'react'
import { Link, useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { hasTaskTag, newId } from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import { useAreas, type AreaRow } from '../data/geo.ts'
import { useAssets } from '../data/fleet.ts'
import { useSlotViews } from '../data/meters.ts'
import { procedureOf, settingLines, useDocumentVersions, usePlan, useTemplateVersions } from '../data/plan.ts'
import { areaShort, machineLabel, sortedAreaIds } from '../data/planUi.ts'
import { useChecklistVersions, useWorkOrders } from '../data/safety.ts'
import { formatClock, formatDuration, prestartState, sessionMs, sessionTitle, useCompletions, useCoverage, useInspections, useWorkIntervals, useWorkSessions } from '../data/work.ts'
import { formatLocal } from '../lib/time.ts'
import { isOpen } from '../sync/projectors.ts'
import { BackLink, Banner, Pill } from '../ui/components.tsx'
import { useAction, useWriteAccess } from '../ui/hooks.ts'
import { useNow } from '../ui/useNow.ts'
import { Picto } from '../ui/pictos.tsx'
import { PhotoField, PhotoStrip } from '../photos/PhotoField.tsx'
import { usePhotoDraft } from '../photos/usePhotos.ts'
import { AvailabilityBanner } from './SafetyPanel.tsx'
import { PrestartLine } from './WorkStart.tsx'
import { EachGreen, SettingLinesView, frameOfAreas } from './plan/JobSettings.tsx'
import { QuickReadings } from './MeterPanel.tsx'
import { MachineSetupLines } from './CuttingSetup.tsx'
import { PinsCard } from './work/PinsCard.tsx'
import { ApplyForm } from './treatments/Apply.tsx'
import { AreaGuideList } from '../ui/AreaGuideList.tsx'
import { LiveShareBanner } from '../ui/LiveShareBanner.tsx'
import { AckPill } from '../ui/AckPill.tsx'

/** The timer (GK-016). Pause and resume are facts like start: the clock follows this device's own record (the server's rows plus what is waiting to be sent), and a pause
 *  stops the clock for good: the time on screen is the sum of the intervals, never "now minus the start". A session opened on a repair order (M23 item 44) is named after it. */
export function WorkPage() {
  const { t, i18n } = useTranslation()
  const { send, site, kiosk, personId, can } = useClub()
  const { sessionId } = useParams({ strict: false }) as { sessionId: string }
  const lang = i18n.language
  const access = useWriteAccess('job.execute')
  const sessions = useWorkSessions(); const intervals = useWorkIntervals()
  const { ready, plan, taskTypes } = usePlan(lang)
  const assets = useAssets(); const areas = useAreas(); const versions = useTemplateVersions(); const docs = useDocumentVersions(); const checklists = useChecklistVersions(); const inspections = useInspections()
  const orders = useWorkOrders()
  const completions = useCompletions(); const coverage = useCoverage()
  const found = sessions?.find((s) => s.id === sessionId)
  const sessionJob = found?.job_id ?? null
  /** the machine's open counters (M23 item 53: the reading block only when there is a counter to read) */
  const slots = useSlotViews(found?.asset_id ?? '')
  /** areas a finish of this job already covered in full (another session, another day): they stay in the list, without the guidance */
  const doneBefore = useMemo(() => {
    if (!sessionJob || !completions || !coverage) return new Set<string>()
    const of = new Set(completions.filter((c) => c.job_id === sessionJob).map((c) => c.id))
    return new Set(coverage.filter((c) => of.has(c.completion_id) && c.coverage === 'full').map((c) => c.area_id))
  }, [sessionJob, completions, coverage])
  const now = useNow()
  const { busy, error, run } = useAction()
  const resumeId = useRef(newId())
  const photos = usePhotoDraft({ entity_type: 'ops.work_session', entity_id: sessionId })
  if (!sessions || !intervals || !ready || !assets || !areas || !versions || !docs) return <p>{t('common.loading')}</p>
  const session = sessions.find((s) => s.id === sessionId)
  if (!session) return <section className="flex flex-col gap-3"><BackLink to="/">← {t('common.today')}</BackLink><Banner tone="warn"><span data-testid="work-missing">{t('work.sessionMissing')}</span></Banner></section>
  const own = intervals.filter((i) => i.work_session_id === session.id)
  const live = own.find((i) => isOpen(i) && !i.superseded_by_id)
  const closed = !!session.closed_at
  const job = session.job_id ? plan.byId.get(session.job_id) : undefined
  const asset = session.asset_id ? assets.find((a) => a.id === session.asset_id) : undefined
  const areaById = new Map(areas.map((a) => [a.id, a as AreaRow]))
  const tv = versions.find((v) => v.id === (session.template_version_id ?? job?.template_version_id))
  const { used } = procedureOf(session.procedure_version_id ?? tv?.procedure_document_version_id, docs)
  const tt = taskTypes.find((x) => x.code === job?.task_type_code)
  const ms = sessionMs(own, now)
  const waiting = !!session._pending || own.some((i) => i._pending)
  const knowledge = session.asset_id ? { asset_id: session.asset_id } : {}
  const pause = () => run(async () => { await send('job.pause@1', { session_id: session.id, ...knowledge }, { intentKey: `pause|${live?.id ?? ''}` }) })
  const resume = () => run(async () => { const id = resumeId.current; resumeId.current = newId(); await send('job.resume@1', { session_id: session.id, interval_id: id, ...knowledge }) })
  const check = prestartState(asset, checklists, inspections, session.asset_id ?? '', site.timezone)
  const hasCounter = (slots ?? []).some((v) => v.open)
  /** M16-C: a job whose kind of work carries the tag `pins` gets the flag-placement card (C's pages/work/PinsCard.tsx, placed by J under the job card while the
   *  timer is open; the page says so in data-pins, which C's pins.spec waits for) */
  const pinsJob = !!job && hasTaskTag(tt, 'pins')
  return (
    <section className="flex flex-col gap-4" data-testid="work-page" data-session={session.id} data-pins={pinsJob ? '1' : '0'}>
      <BackLink to="/">← {t('common.today')}</BackLink>
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-2xl font-bold" data-testid="work-title">{sessionTitle(session, job?.title_text, orders, assets, t)}</h1>
        <Pill tone={closed ? 'gray' : live ? 'green' : 'amber'}><span data-testid="work-state">{closed ? t('work.state.finished') : live ? t('work.state.running') : t('work.state.paused')}</span></Pill>
        {waiting && <Pill tone="amber"><span data-testid="work-waiting">{t('common.pendingSend')}</span></Pill>}
      </div>
      <div className="card flex flex-col items-center gap-1 py-6" data-testid="timer-card">
        <span className="font-mono text-6xl font-bold tabular-nums" data-testid="timer" role="timer" aria-live="off">{formatClock(ms)}</span>
        <span className="meta">{t('work.startedAt', { when: formatLocal(session.started_at, site.timezone, 'HH:mm') })} · {t('work.worked', { time: formatDuration(ms) })}</span>
      </div>
      {!closed && (
        <div className="grid grid-cols-2 gap-3">
          {live
            ? <button className="btn text-xl" disabled={busy || !access.allowed} onClick={() => void pause()} data-testid="work-pause">{t('work.pause')}</button>
            : <button className="btn btn-primary text-xl" disabled={busy || !access.allowed} onClick={() => void resume()} data-testid="work-resume">{t('work.resume')}</button>}
          <Link to="/work/$sessionId/finish" params={{ sessionId: session.id }} className="btn btn-primary text-xl" data-testid="work-finish">{t('work.finish')}</Link>
        </div>
      )}
      {live && session.person_id === personId && <LiveShareBanner sessionId={session.id} />}
      {closed && <Banner tone="ok"><span data-testid="work-closed">{t('work.closedNotice', { time: formatDuration(ms) })}</span></Banner>}
      {error && <Banner tone="error">{error}</Banner>}
      {job && (
        <div className="card flex flex-col gap-2">
          {job.areaIds.length > 0 && (closed
            ? <ul className="flex flex-wrap gap-2" data-testid="work-areas">{sortedAreaIds(job.areaIds, areaById, lang).map((id) => <li key={id} className="rounded-lg border-2 border-slate-300 px-3 py-1 text-sm font-medium dark:border-slate-600">{areaShort(areaById.get(id), areaById, lang, t)}</li>)}</ul>
            : <AreaGuideList ids={sortedAreaIds(job.areaIds, areaById, lang)} areaById={areaById} lang={lang} guide={!kiosk} done={doneBefore} testId="work-areas" />)}
          {Object.keys(job.params).length > 0 && <SettingLinesView lines={settingLines(job.params, tt, lang)} frame={frameOfAreas(job.areaIds, areaById)} className="text-sm" testId="work-params" />}
          {job.notes && <p className="whitespace-pre-line text-sm">{job.notes}</p>}
          {/* M23 item 47: "Procédure" was a 130×20 link; a 56-px row under the zones now */}
          {used && <Link to="/procedures/$versionId" params={{ versionId: used.id }} search={{ from: `work:${session.id}` }} className="btn w-full" data-testid="work-procedure"><Picto name="clipboard" className="size-5" />{t('work.procedureRead')}</Link>}
          {/* M16-I2: "version non lue" under the procedure's row, for the person on this device, only when the procedure asks for a reading (never a refusal) */}
          {used && <AckPill versionId={used.id} testId="work-ack-pill" />}

        </div>
      )}
      {/* M25-D: « Sur chaque green » — the real outlines turned to their line of play, the stripes at the job's direction (a fold, closed until asked) */}
      {job && !closed && <EachGreen areaIds={job.areaIds} lines={settingLines(job.params, tt, lang)} areaById={areaById} lang={lang} testId="work-each-green" />}
      {/* M15-C: "Consigner un traitement" from the timer: the register's fields, offline (a fold here, as A's fill-up on the machine page; the page is Z's route from the finish).
          Never on the shared workstation (the Phase D decision, M15a review: the server refuses it KIOSK_NOT_ALLOWED, and can() does not offer treatment.record there either) */}
      {job && !closed && !kiosk && can('treatment.record') && (
        <details id="apply" className="card" data-testid="work-apply-fold">
          <summary className="flex min-h-14 cursor-pointer items-center gap-2 text-lg font-bold" data-testid="work-apply-toggle"><Picto name="sprayer" className="size-5" />{t('stock.finish.apply')}</summary>
          <div className="pt-3"><ApplyForm session={session} job={job} /></div>
        </details>
      )}
      {/* M16-C: "Emplacements des trous", one card per green of the job, while the timer is open (the planning page keeps the history after) */}
      {pinsJob && !closed && job && <PinsCard jobId={job.id} areaIds={job.areaIds} />}
      <div className="card flex flex-col gap-2" data-testid="work-photos">
        <h2 className="text-lg font-bold">{t('photos.sectionTitle')}</h2>
        <PhotoStrip entityId={session.id} />
        <PhotoField draft={photos} disabled={!access.allowed} />
      </div>
      {asset && (
        <div className="card flex flex-col gap-2" data-testid="work-machine">
          <h2 className="text-lg font-bold">{machineLabel(asset)}</h2>
          <AvailabilityBanner assetId={asset.id} />
          <PrestartLine asset={asset} check={check} tz={site.timezone} />
          {/* M16-J: the last measured cutting setup of the machine and of what is mounted on it ("réglée à 3,2 mm le …"); the job's target is in its settings above */}
          <MachineSetupLines machineId={asset.id} />
          {!closed && (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Link to="/assets/$assetId/defect" params={{ assetId: asset.id }} className="btn" data-testid="work-defect">{t('defect.report')}</Link>
              <Link to="/assets/$assetId" params={{ assetId: asset.id }} className="btn">{t('work.machinePage')}</Link>
              {/* M16a review: the setup just measured is noted from here (the panel's #cutting, reached once the page has settled: AssetDetail) */}
              {can('setup.record') && <Link to="/assets/$assetId" params={{ assetId: asset.id }} hash="cutting" className="btn sm:col-span-2" data-testid="work-cutting">{t('cutting.title')}</Link>}
            </div>
          )}
          {!closed && hasCounter && <QuickReadings assetId={asset.id} />}
        </div>
      )}
    </section>
  )
}
